import React, { useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from '@/components/Dashboard';
import TenderList from '@/components/TenderList';
import ContractList from '@/components/ContractList';
import PortalManager from '@/components/PortalManager';
import SettingsPage from '@/components/Settings';
import Agenda from '@/components/Agenda';
import DocumentGuide from '@/components/DocumentGuide';
import OrderManager from '@/components/OrderManager';
import OrderBoard from '@/components/OrderBoard';
import Login from '@/components/Login';
import Register from '@/components/Register';
import ForgotPassword from '@/components/ForgotPassword';
import ResetPassword from '@/components/ResetPassword';
import CommunicationManager from '@/components/CommunicationManager';
import ProductManager from '@/components/ProductManager';
import ChatMonitoring from '@/components/ChatMonitoring';
import OpportunitySearch from '@/components/OpportunitySearch';
import CertificateList from '@/components/CertificateList';
import GanhasPage from '@/components/GanhasPage';
import ResultadosLicitacoes from '@/components/ResultadosLicitacoes';
import ConcorrentesResultados from '@/components/ConcorrentesResultados';
import Participation from '@/components/Participation';
import TenderWorkflow from '@/components/TenderWorkflow';
import OpportunityAnalyst from '@/components/OpportunityAnalyst';
import TenderOpportunityCalendar from '@/components/TenderOpportunityCalendar';
import LegalManager from '@/components/LegalManager';
import ModalProvider from '@/components/ModalProvider';
import { AppShell } from '@/components/layout/AppShell';
import { getApiUrl } from '@/config/api';
import { hasMenuPermission, MenuPermissionKey } from '@/config/menuPermissions';

const readStoredUser = () => {
  const rawUser = localStorage.getItem('user');
  if (!rawUser) return null;

  try {
    return JSON.parse(rawUser);
  } catch {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    return null;
  }
};

// Hook para verificar autenticação
const useAuth = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(readStoredUser);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    fetch(`${getApiUrl()}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('Sessão inválida');
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        const currentUser = data.user || data;
        localStorage.setItem('user', JSON.stringify(currentUser));
        setUser(currentUser);
      })
      .catch(() => {
        if (cancelled) return;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  return { isAuthenticated: !!token && !!user, user, loading };
};

const AuthenticatedShell: React.FC<{ children: React.ReactNode; user: any }> = ({ children, user }) => {
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.hash = '/login';
  };

  const handleOpenAi = () => {
    window.location.hash = '/analyst';
  };

  return (
    <AppShell user={user} onLogout={handleLogout} onOpenAi={handleOpenAi}>
      {children}
    </AppShell>
  );
};

const PermissionRoute: React.FC<{
  user: { role?: string; menuKeys?: string[] } | null;
  permission: MenuPermissionKey;
  children: React.ReactNode;
}> = ({ user, permission, children }) => {
  if (hasMenuPermission(user, permission)) return <>{children}</>;
  return (
    <div className="min-h-[50vh] grid place-items-center p-6 text-center text-ink-subtitle dark:text-ink-subtitle-dark">
      Seu perfil não possui acesso a este menu.
    </div>
  );
};

const AuthenticatedRoutes: React.FC = () => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-ink-subtitle dark:text-ink-subtitle-dark">Carregando sessão...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const guarded = (permission: MenuPermissionKey, element: React.ReactNode) => (
    <PermissionRoute user={user} permission={permission}>{element}</PermissionRoute>
  );

  return (
    <ModalProvider>
      <AuthenticatedShell user={user}>
        <Routes>
          <Route path="/" element={guarded('DASHBOARD', <Dashboard />)} />
          <Route path="/agenda" element={guarded('AGENDA', <Agenda />)} />
          <Route path="/tenders" element={guarded('OPPORTUNITIES', <TenderList />)} />
          <Route path="/favorites" element={guarded('FAVORITES', <OpportunitySearch initialView="FAVORITED" />)} />
          <Route path="/favorites-archived" element={guarded('FAVORITES_ARCHIVED', <OpportunitySearch initialView="ARCHIVED" />)} />
          <Route path="/ongoing" element={guarded('ONGOING', <TenderWorkflow mode="ONGOING" />)} />
          <Route path="/participation" element={guarded('PARTICIPATION', <Participation />)} />
          <Route path="/consults" element={guarded('OPPORTUNITIES', <OpportunitySearch />)} />
          <Route path="/tender-calendar" element={guarded('OPPORTUNITIES', <TenderOpportunityCalendar />)} />
          <Route path="/analyst" element={guarded('ANALYST', <OpportunityAnalyst />)} />
          <Route path="/competitors" element={guarded('COMPETITORS', <TenderList viewMode="CONCORRENTE" />)} />
          <Route path="/ganhas" element={guarded('HOMOLOGATED', <GanhasPage />)} />
          <Route path="/resultados" element={guarded('RESULTS', <ResultadosLicitacoes />)} />
          <Route path="/concorrentes-resultados" element={guarded('COMPETITOR_RESULTS', <ConcorrentesResultados />)} />
          <Route path="/products" element={guarded('PRODUCTS', <ProductManager />)} />
          <Route path="/orders" element={guarded('ORDERS', <OrderManager />)} />
          <Route path="/orders/board" element={guarded('ORDERS', <OrderBoard />)} />
          <Route path="/communications" element={guarded('COMMUNICATIONS', <CommunicationManager />)} />
          <Route path="/legal" element={guarded('LEGAL', <LegalManager />)} />
          <Route path="/chat-monitoring" element={guarded('CHAT_MONITORING', <ChatMonitoring />)} />
          <Route path="/contracts" element={guarded('CONTRACTS', <ContractList />)} />
          <Route path="/atestados" element={guarded('CERTIFICATES', <CertificateList />)} />
          <Route path="/documents" element={guarded('DOCUMENTS', <DocumentGuide />)} />
          <Route path="/portals" element={guarded('PORTALS', <PortalManager />)} />
          <Route path="/ai-analysis" element={guarded('ANALYST', <Navigate to="/analyst" replace />)} />
          <Route path="/templates" element={guarded('PARTICIPATION', <Navigate to="/participation" replace />)} />
          <Route path="/settings" element={guarded('SETTINGS', <SettingsPage />)} />
          <Route
            path="*"
            element={
              <div className="p-6 text-center text-ink-subtitle dark:text-ink-subtitle-dark">
                Página não encontrada
              </div>
            }
          />
        </Routes>
      </AuthenticatedShell>
    </ModalProvider>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Rotas Públicas — fora do AppShell */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Rotas Privadas — dentro do AppShell */}
        <Route
          path="/*"
          element={<AuthenticatedRoutes />}
        />
      </Routes>
    </Router>
  );
};

export default App;
